import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, TrendingUp, ShieldCheck, BadgeCheck } from "lucide-react";
import StockBackground from "./StockBackground";

export default function Hero({ onPrimary, onSecondary }) {
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 700], [0, 130]);
  const parallaxOpacity = useTransform(scrollY, [0, 550], [1, 0.25]);
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-20 overflow-hidden bg-[#050505]"
    >
      <StockBackground />
      {/* Dark vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-transparent to-[#050505]" />
      <div className="absolute inset-0 bg-grid opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20">
        <motion.div style={{ y: parallaxY, opacity: parallaxOpacity }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs tracking-[0.25em] uppercase text-[#D4AF37]"
          data-testid="hero-eyebrow"
        >
          <BadgeCheck size={14} /> AMFI Registered Mutual Fund Distributor &middot; ARN-365843
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display font-bold text-white mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight max-w-5xl"
        >
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block will-change-transform"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              Intelligent Investing.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block text-gold-gradient will-change-transform"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.34, ease: [0.16, 1, 0.3, 1] }}
            >
              Enduring Results.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          data-testid="hero-subheading"
          className="mt-8 max-w-2xl text-base lg:text-lg text-gray-300/90 leading-relaxed"
        >
          Helping Individuals, Families and Businesses build long-term wealth through disciplined investing,
          research-driven portfolio construction and personalized investor support.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={onPrimary}
            data-testid="hero-cta-consultation"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#D4AF37] text-black font-semibold text-base hover:bg-[#FFD700] transition shadow-[0_15px_45px_-10px_rgba(212,175,55,0.55)]"
          >
            Schedule Consultation
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={onSecondary}
            data-testid="hero-cta-invest"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full glass text-white font-semibold text-base border-white/15 hover:border-[#D4AF37]/60 transition"
          >
            Start Investing
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl"
          data-testid="hero-trust-badges"
        >
          {[
            { icon: <ShieldCheck size={16} />, label: "AMFI Registered" },
            { icon: <TrendingUp size={16} />, label: "Research-Driven" },
            { icon: <BadgeCheck size={16} />, label: "Transparent Suggestions" },
            { icon: <ShieldCheck size={16} />, label: "Client-First Approach" },
          ].map((b, i) => (
            <div key={i} className="glass rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-gray-200">
              <span className="text-[#D4AF37]">{b.icon}</span>
              {b.label}
            </div>
          ))}
        </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
